import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  TextField,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import debounce from "lodash/debounce";
import TablePagination from "../TablePagination";
import { useQuests, usePromotions } from "@quest_editor/mocks";
import { getQuestStatusOptions } from "@quest_editor/utils/view/constLabels";

const rowsPerPage = 10;

type QuestSearchDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (questIds: string[]) => void;
  maxSelectable?: number;
  initialSelectedIds?: string[];
};

function QuestSearchDialog({
  open,
  onClose,
  onSelect,
  maxSelectable = Infinity,
  initialSelectedIds = [],
}: QuestSearchDialogProps) {
  const { t } = useTranslation("questEditor");
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);

  const { data: questsData } = useQuests();
  const quests = questsData?.quests ?? [];

  const { data: promotionsData } = usePromotions();
  const promotions = promotionsData?.promotions ?? [];

  const questStatuses = getQuestStatusOptions();

  const getPromotionName = (promotionId: string) =>
    promotions.find((p) => p.id === promotionId)?.name ?? "";

  const getStatusLabel = (status: string) =>
    questStatuses.find((s) => s.value === status)?.label ?? status;

  const filteredQuests = useMemo(() => {
    if (!searchText) return quests;
    const lowerSearch = searchText.toLowerCase();
    return quests.filter(
      (quest) =>
        quest.id.toLowerCase().includes(lowerSearch) ||
        quest.name.toLowerCase().includes(lowerSearch) ||
        getPromotionName(quest.promotionId).toLowerCase().includes(lowerSearch),
    );
  }, [searchText, quests]);

  const totalPages = Math.ceil(filteredQuests.length / rowsPerPage);
  const paginatedQuests = filteredQuests.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage,
  );

  const handleToggle = (questId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(questId)) {
        return prev.filter((id) => id !== questId);
      }
      if (maxSelectable === 1) {
        return [questId];
      }
      if (prev.length >= maxSelectable) {
        return prev;
      }
      return [...prev, questId];
    });
  };

  const handleConfirm = () => {
    const questIdOrder = quests.map((q) => q.id);
    const sortedIds = [...selectedIds].sort(
      (a, b) => questIdOrder.indexOf(a) - questIdOrder.indexOf(b),
    );
    onSelect(sortedIds);
    onClose();
  };

  const handleClose = () => {
    setSelectedIds(initialSelectedIds);
    onClose();
  };

  const performSearch = useCallback((value: string) => {
    setSearchText(value);
    setPage(0);
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => performSearch(value), 300),
    [performSearch],
  );

  const [inputValue, setInputValue] = useState("");

  const handleSearchChange = (value: string) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("quest_edit_target.field.quest.dialog.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder={t(
                "quest_edit_target.field.quest.dialog.search_placeholder",
              )}
              value={inputValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ width: 300 }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {isFinite(maxSelectable)
                ? t("quest_edit_target.field.quest.dialog.selected_count", {
                    count: selectedIds.length,
                    max: maxSelectable,
                  })
                : t(
                    "quest_edit_target.field.quest.dialog.selected_count_infinity",
                    {
                      count: selectedIds.length,
                    },
                  )}
            </Typography>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    {t("quest_edit_target.field.quest.dialog.table.id")}
                  </TableCell>
                  <TableCell>
                    {t("quest_edit_target.field.quest.dialog.table.name")}
                  </TableCell>
                  <TableCell>
                    {t("quest_edit_target.field.quest.dialog.table.promotion")}
                  </TableCell>
                  <TableCell>
                    {t("quest_edit_target.field.quest.dialog.table.status")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedQuests.map((quest) => {
                  const isSelected = selectedIds.includes(quest.id);
                  const isDisabled =
                    !isSelected &&
                    maxSelectable > 1 &&
                    selectedIds.length >= maxSelectable;

                  return (
                    <TableRow
                      key={quest.id}
                      hover
                      onClick={() => !isDisabled && handleToggle(quest.id)}
                      sx={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} disabled={isDisabled} />
                      </TableCell>
                      <TableCell>{quest.id}</TableCell>
                      <TableCell>{quest.name}</TableCell>
                      <TableCell>{getPromotionName(quest.promotionId)}</TableCell>
                      <TableCell>{getStatusLabel(quest.status)}</TableCell>
                    </TableRow>
                  );
                })}
                {paginatedQuests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t("quest_edit_target.field.quest.dialog.no_results")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredQuests.length > 0 && (
            <TablePagination
              total={filteredQuests.length}
              page={page}
              setPage={setPage}
              count={totalPages}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <BackButton onClick={handleClose}>
          {t("quest_edit_target.field.quest.dialog.button.cancel")}
        </BackButton>
        <ActionButton
          onClick={handleConfirm}
          disabled={selectedIds.length === 0}
        >
          {t("quest_edit_target.field.quest.dialog.button.confirm")}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

export default QuestSearchDialog;
